import React, { createContext, useContext, useState, useEffect } from "react";
import axios, { AxiosError } from "axios";
import { useSeason } from "./SeasonContext";
import { useClub } from "./ClubContext";
import { DepthChart, Side } from "../types/DepthChartTypes";
import { AllSideTabs, API_BASE_URL } from "../constants";
import { RosterPlayer } from "../types/LogTypes";

interface DepthChartContextType {
  // Core state management
  isLoading: boolean;
  error: string | null;
  success: boolean;
  editMode: boolean;
  setEditMode: (editMode: boolean) => void;

  // Depth chart selection and management
  selectedDepthChart: DepthChart | null;
  setSelectedDepthChart: React.Dispatch<
    React.SetStateAction<DepthChart | null>
  >;
  selectedSide: Side;
  setSelectedSide: (side: Side) => void;
  depthCharts: DepthChart[];
  setDepthCharts: (depthCharts: DepthChart[]) => void;
  selectedTab: number;
  setSelectedTab: (tab: number) => void;
  templateDepthChart: DepthChart | null;
  setTemplateDepthChart: (templateDepthChart: DepthChart | null) => void;

  // API Functions
  handleTabChange: (newSide: Side) => Promise<void>;
  saveDepthChart: () => Promise<void>;
  deleteDepthChart: (id: number) => Promise<void>;
  fetchTemplateDepthChart: (newSide: Side) => Promise<DepthChart>;
  createNewFromTemplate: (side: Side) => Promise<void>;
  fetchDepthCharts: (
    selectedClubId: number,
    selectedSeasonId: number,
    selectedSide: Side
  ) => Promise<DepthChart[]>;
  addDepthChartToGame: (chartId: number, gameId: number) => Promise<void>;
  getDepthChartsForGame: (gameId: number) => Promise<void>;

  // Full Unit Groups (11 players)
  offensePlayers: RosterPlayer[];
  setOffensePlayers: (offensePlayers: RosterPlayer[]) => void;
  defensePlayers: RosterPlayer[];
  setDefensePlayers: (defensePlayers: RosterPlayer[]) => void;
  kickOffReturnPlayers: RosterPlayer[];
  setKickOffReturnPlayers: (players: RosterPlayer[]) => void;
  kickoffPlayers: RosterPlayer[];
  setKickoffPlayers: (players: RosterPlayer[]) => void;
  puntPlayers: RosterPlayer[];
  setPuntPlayers: (players: RosterPlayer[]) => void;
  puntReturnPlayers: RosterPlayer[];
  setPuntReturnPlayers: (players: RosterPlayer[]) => void;
  fieldGoalPlayers: RosterPlayer[];
  setFieldGoalPlayers: (players: RosterPlayer[]) => void;
  fieldGoalBlockPlayers: RosterPlayer[];
  setFieldGoalBlockPlayers: (players: RosterPlayer[]) => void;

  // Specialized Player Groups
  quarterbacks: RosterPlayer[];
  setQuarterbacks: (players: RosterPlayer[]) => void;
  ballCarriers: RosterPlayer[];
  setBallCarriers: (players: RosterPlayer[]) => void;
  receivers: RosterPlayer[];
  setReceivers: (players: RosterPlayer[]) => void;
  kickOffReturners: RosterPlayer[];
  setKickOffReturners: (players: RosterPlayer[]) => void;
  puntReturners: RosterPlayer[];
  setPuntReturners: (players: RosterPlayer[]) => void;
  kickOffKickers: RosterPlayer[];
  setKickOffKickers: (players: RosterPlayer[]) => void;
  punters: RosterPlayer[];
  setPunters: (players: RosterPlayer[]) => void;
  fieldGoalKickers: RosterPlayer[];
  setFieldGoalKickers: (players: RosterPlayer[]) => void;
}

const DepthChartContext = createContext<DepthChartContextType | undefined>(
  undefined
);

export const DepthChartProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { selectedSeason } = useSeason();
  const { club } = useClub();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedDepthChart, setSelectedDepthChart] =
    useState<DepthChart | null>(null);
  const [selectedSide, setSelectedSide] = useState<Side>("OFFENSE");
  const [depthCharts, setDepthCharts] = useState<DepthChart[]>([]);
  const [templateDepthChart, setTemplateDepthChart] =
    useState<DepthChart | null>(null);
  const [selectedTab, setSelectedTab] = useState<number>(0);

  // For a specific game, we need to group the players by position
  // For penalty logs, we need all 11 who are likely to be on the field
  const [offensePlayers, setOffensePlayers] = useState<RosterPlayer[]>([]);
  const [defensePlayers, setDefensePlayers] = useState<RosterPlayer[]>([]);
  const [kickOffReturnPlayers, setKickOffReturnPlayers] = useState<
    RosterPlayer[]
  >([]);
  const [kickoffPlayers, setKickoffPlayers] = useState<RosterPlayer[]>([]);
  const [puntPlayers, setPuntPlayers] = useState<RosterPlayer[]>([]);
  const [puntReturnPlayers, setPuntReturnPlayers] = useState<RosterPlayer[]>(
    []
  );
  const [fieldGoalPlayers, setFieldGoalPlayers] = useState<RosterPlayer[]>([]);
  const [fieldGoalBlockPlayers, setFieldGoalBlockPlayers] = useState<
    RosterPlayer[]
  >([]);

  // For other log types, we need to get the players who are likely to be interacting with the ball
  const [kickOffReturners, setKickOffReturners] = useState<RosterPlayer[]>([]);
  const [puntReturners, setPuntReturners] = useState<RosterPlayer[]>([]);
  const [quarterbacks, setQuarterbacks] = useState<RosterPlayer[]>([]);
  const [ballCarriers, setBallCarriers] = useState<RosterPlayer[]>([]);
  const [receivers, setReceivers] = useState<RosterPlayer[]>([]);
  const [kickOffKickers, setKickOffKickers] = useState<RosterPlayer[]>([]);
  const [punters, setPunters] = useState<RosterPlayer[]>([]);
  const [fieldGoalKickers, setFieldGoalKickers] = useState<RosterPlayer[]>([]);

  // Load depth charts for the given season and club
  useEffect(() => {
    if (selectedSeason && club) {
      fetchDepthCharts(club.id, selectedSeason.id, selectedSide);
      fetchTemplateDepthChart(selectedSide);
    }
  }, [selectedSeason, club]);

  const fetchTemplateDepthChart = async (
    newSide: Side
  ): Promise<DepthChart> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/depth-charts/get_template/`,
        {
          params: { side: newSide, season_id: selectedSeason?.id },
        }
      );
      // setSelectedDepthChart(response.data);
      setTemplateDepthChart(response.data);
      return response.data;
    } catch (err: unknown) {
      const error = err as AxiosError;
      setError(error.message || "Error fetching template depth chart");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const saveDepthChart = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    if (!selectedDepthChart) {
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/depth-charts/`, {
        name: selectedDepthChart.name,
        side: selectedDepthChart.side,
        personnel_group: selectedDepthChart.personnel_group,
        game: selectedDepthChart.game,
        positions: selectedDepthChart.positions,
        season: selectedSeason?.id,
        club: club?.id,
        is_template: selectedDepthChart.is_template,
      });
    } catch (err: unknown) {
      const error = err as AxiosError;
      setError(error.message || "Error saving depth chart");
      throw err;
    } finally {
      if (club && selectedSeason) {
        fetchDepthCharts(club.id, selectedSeason.id, selectedSide);
      }
      setIsLoading(false);
    }
  };

  const fetchDepthCharts = async (
    selectedClubId: number,
    selectedSeasonId: number,
    selectedSide: Side
  ): Promise<DepthChart[]> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get<DepthChart[]>(
        `${API_BASE_URL}/depth-charts/`,
        {
          params: {
            club: selectedClubId,
            season: selectedSeasonId,
            side: selectedSide,
            is_template: false,
          },
        }
      );
      setDepthCharts(response.data);
      return response.data;
    } catch (err: unknown) {
      const error = err as AxiosError;
      setError(error.message || "Error fetching depth charts");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteDepthChart = async (id: number): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/depth-charts/${id}/`);
      setDepthCharts(depthCharts.filter((depthChart) => depthChart.id !== id));
    } catch (err: unknown) {
      const error = err as AxiosError;
      setError(error.message || "Error deleting depth chart");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = async (newSide: Side) => {
    setIsLoading(true);
    try {
      setSelectedTab(AllSideTabs.findIndex((tab) => tab.value === newSide));
      setSelectedSide(newSide);
      setSelectedDepthChart(null);
      setEditMode(false);

      if (selectedSeason && club) {
        await Promise.all([
          fetchTemplateDepthChart(newSide),
          fetchDepthCharts(club.id, selectedSeason.id, newSide),
        ]);
      }
    } catch (err: unknown) {
      const error = err as AxiosError;
      setError(error.message || "Error fetching depth charts");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const createNewFromTemplate = async (side: Side) => {
    setIsLoading(true);
    setError(null);
    try {
      await fetchTemplateDepthChart(side);

      if (!templateDepthChart) {
        throw new Error("Failed to load template depth chart");
      }

      // Create a new chart based on the template
      const newChart: DepthChart = {
        ...templateDepthChart,
        id: undefined, // This will be set by the backend
        name: `New ${side} Chart`,
        is_template: false,
        created_at: new Date().toISOString(),
      };

      // Set this as the selected chart
      setSelectedDepthChart(newChart);
    } catch (err: unknown) {
      const error = err as AxiosError;
      setError(error.message || "Error creating new chart from template");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const addDepthChartToGame = async (chartId: number, gameId: number) => {
    try {
      const response = await axios
        .post(`${API_BASE_URL}/depth-charts/add_to_game/`, {
          depth_chart_id: chartId,
          game_id: gameId,
        })
        .then(() => {
          if (selectedSeason && club) {
            fetchDepthCharts(club.id, selectedSeason.id, selectedSide);
          }
        });
    } catch (err: unknown) {
      const error = err as AxiosError;
      setError(error.message || "Error adding depth chart to game");
      throw err;
    }
  };

  const getDepthChartsForGame = async (gameId: number) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/depth-charts/for_game/`,
        {
          params: { game_id: gameId },
        }
      );

      setOffensePlayers(response.data.offense_players);
      setDefensePlayers(response.data.defense_players);
      setKickOffReturnPlayers(response.data.kickoff_return_players);
      setKickoffPlayers(response.data.kickoff_players);
      setPuntPlayers(response.data.punt_players);
      setPuntReturnPlayers(response.data.punt_return_players);
      setFieldGoalPlayers(response.data.field_goal_players);
      setFieldGoalBlockPlayers(response.data.field_goal_block_players);

      setQuarterbacks(response.data.quarterbacks);
      setBallCarriers(response.data.ball_carriers);
      setReceivers(response.data.receivers);
      setKickOffReturners(response.data.kickoff_returners);
      setPuntReturners(response.data.punt_returners);
      setKickOffKickers(response.data.kickoff_kickers);
      setPunters(response.data.punters);
      setFieldGoalKickers(response.data.field_goal_kickers);
    } catch (err: unknown) {
      const error = err as AxiosError;
      setError(error.message || "Error fetching depth charts for game");
      throw err;
    }
  };

  return (
    <DepthChartContext.Provider
      value={{
        // Core state management
        isLoading,
        error,
        success,
        editMode,
        setEditMode,

        // Depth chart selection and management
        selectedDepthChart,
        setSelectedDepthChart,
        selectedSide,
        setSelectedSide,
        depthCharts,
        setDepthCharts,
        selectedTab,
        setSelectedTab,
        templateDepthChart,
        setTemplateDepthChart,

        // API Functions
        handleTabChange,
        saveDepthChart,
        deleteDepthChart,
        fetchTemplateDepthChart,
        createNewFromTemplate,
        fetchDepthCharts,
        addDepthChartToGame,
        getDepthChartsForGame,

        // Full Unit Groups (11 players)
        offensePlayers,
        setOffensePlayers,
        defensePlayers,
        setDefensePlayers,
        kickOffReturnPlayers,
        setKickOffReturnPlayers,
        kickoffPlayers,
        setKickoffPlayers,
        puntPlayers,
        setPuntPlayers,
        puntReturnPlayers,
        setPuntReturnPlayers,
        fieldGoalPlayers,
        setFieldGoalPlayers,
        fieldGoalBlockPlayers,
        setFieldGoalBlockPlayers,

        // Specialized Player Groups
        quarterbacks,
        setQuarterbacks,
        ballCarriers,
        setBallCarriers,
        receivers,
        setReceivers,
        kickOffReturners,
        setKickOffReturners,
        puntReturners,
        setPuntReturners,
        kickOffKickers,
        setKickOffKickers,
        punters,
        setPunters,
        fieldGoalKickers,
        setFieldGoalKickers,
      }}
    >
      {children}
    </DepthChartContext.Provider>
  );
};

export const useDepthChart = () => {
  const context = useContext(DepthChartContext);
  if (context === undefined) {
    throw new Error("useDepthChart must be used within a DepthChartProvider");
  }
  return context;
};
